<template>
  <footer aria-label="Footer">
    <!-- Top footer -->
    <div v-if="!compact" class="bg-[--footer-top-bg-color] text-[--footer-top-text-color] md:block print:!hidden">
      <div class="container mx-auto px-5 lg:px-12">
        <div class="flex flex-col items-center pt-3 lg:flex-row lg:pt-12">
          <div class="lg:w-1/4">
            <div class="logo">
              <a :href="landingUrl" target="_blank" aria-label="Go to AIoD Platform home page">
                <VcImage src="/static/images/common/logo.svg" :alt="$context.storeName" lazy />
              </a>
            </div>
          </div>
          <div class="w-full ps-4 pt-3 lg:w-1/2">
            <div class="call-to-action flex flex-col items-center font-medium lg:flex-row">
              <a class="footer-link mb-3 mr-5 inline-block" :href="urlEmail" aria-label="Contact AIoD support">
                {{ $t("shared.layout.footer.contact_us_link") }}
              </a>
              <a
                class="footer-link mb-3 inline-block"
                :href="landingUrl"
                target="_blank"
                aria-label="Explore the AIoD Platform"
              >
                {{ $t("shared.layout.footer.explore_platform_link") }}
              </a>
            </div>
          </div>
          <div class="ml-0 pt-4 text-center lg:w-1/4 lg:flex-auto">
            <a
              class="social-btn icon ml-6 inline-block"
              :href="urlLinkedin"
              aria-label="Follow AIoD on LinkedIn"
              target="_blank"
            >
              <svg class="icon icon-linkedin" width="14" height="14" aria-hidden="true">
                <use :xlink:href="`${sprites}#icon-linkedin`"></use>
              </svg>
            </a>
            <a class="social-btn icon ml-6 inline-block" :href="urlX" aria-label="Follow AIoD on X" target="_blank">
              <svg class="icon icon-x" width="14" height="14" aria-hidden="true">
                <use :xlink:href="`${sprites}#icon-x`"></use>
              </svg>
            </a>
          </div>
        </div>

        <div class="legal flex flex-col items-center pt-3 lg:flex-row lg:pt-12">
          <div class="eu-logo-container lg:w-1/4">
            <img
              class="lazyload mx-auto h-12 w-17"
              loading="lazy"
              width="68"
              height="45"
              :src="flagSrc"
              alt="European Union"
            />
          </div>
          <div class="w-full ps-4 text-start lg:w-1/2">
            <p class="mt-3 lg:mt-0">
              {{ $t("shared.layout.footer.project_agreement_text") }}
            </p>
            <p class="mb-9 mt-2">
              <a target="_blank" :href="imprintUrl">{{ $t("shared.layout.footer.imprint_link") }}</a>
              &middot;
              <a target="_blank" :href="termsURL">{{ $t("shared.layout.footer.terms_and_conditions_link") }}</a>
              &middot;
              <a target="_blank" :href="privacyPolicyUrl">{{ $t("shared.layout.footer.privacy_policy_link") }}</a>
              &middot;
              <a href="#" @click.prevent="openBanner">{{ $t("shared.cookie_consent.settings") }}</a>
              &middot;
              {{ $t("shared.layout.footer.version") }} {{ version }}. © {{ new Date().getFullYear() }}
            </p>
          </div>
        </div>
        <div class="ml-auto w-1/4 text-right lg:flex-auto"></div>
        <template v-if="whiteLabelingFooterLinks?.length">
          <FooterLinks v-for="(footerLink, index) in whiteLabelingFooterLinks" :key="index" :links-block="footerLink" />
        </template>

        <template v-else>
          <FooterLinks v-for="footerLink in footerLinks" :key="footerLink.id" :links-block="footerLink" />
        </template>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import flagSrc from "@/assets/images/symbols/flag-eu-yellow.svg";
import sprites from "@/assets/images/symbols/sprites.svg";
import { useNavigations, useWhiteLabeling } from "@/core/composables";
import {
  AIODP_URL,
  DEPLOYAI_URL_IMPRINT,
  DEPLOYAI_URL_PRIVACY_POLICY,
  DEPLOYAI_URL_TERMS_AND_CONDITIONS,
  CONTACT_US_LINK,
} from "@/core/constants";
import { useCookieConsent } from "@/shared/cookie-consent";
import pkg from "../../../../../package.json";
import FooterLinks from "./_internal/footer-links.vue";

interface IProps {
  compact?: boolean;
}

defineProps<IProps>();

const { footerLinks: whiteLabelingFooterLinks } = useWhiteLabeling();
const { footerLinks } = useNavigations();
const { openBanner } = useCookieConsent();

const imprintUrl = DEPLOYAI_URL_IMPRINT;
const termsURL = DEPLOYAI_URL_TERMS_AND_CONDITIONS;
const privacyPolicyUrl = DEPLOYAI_URL_PRIVACY_POLICY;
const landingUrl = AIODP_URL;

const { version } = pkg;

const urlLinkedin = "https://www.linkedin.com/company/aiondemand/";
const urlX = "https://x.com/aiondemand";
const urlEmail = CONTACT_US_LINK;
</script>

<style lang="scss" scoped>
footer {
  .logo {
    img {
      width: 100px;
      margin: 0 auto;
    }
  }

  a.icon {
    background-color: var(--clr-white);
    border-radius: 50%;
    transition:
      color 0.15s ease-in-out,
      background-color 0.15s ease-in-out,
      border-color 0.15s ease-in-out;
  }

  a.icon svg {
    width: 0.875rem;
    height: 0.875rem;
    margin: 0.5rem;
    color: var(--clr-blue);
  }

  a:hover,
  a:active {
    color: var(--color-neutral-600);
  }

  .legal {
    color: var(--color-neutral-950);
    font-size: 0.8rem;
    line-height: 1.25;
    font-weight: 400;
  }

  .legal .statement {
    display: block;
    margin-inline: auto;
    max-width: 46rem;
  }

  .call-to-action {
    font-size: 0.875rem;
  }

  .eu-logo-container {
    align-self: start;
  }

  .footer-link {
    text-decoration: underline;
    font-weight: 600;
    color: var(--color-neutral-950);
    font-size: 16px;

    &:hover {
      color: var(--clr-blue);
    }
  }
}
</style>
